/**
 * OpenAI Content Generation Service
 * Connects to Django backend for AI-powered carousel content generation
 */

import axios from 'axios';

// Django backend API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

/**
 * Get authentication token from localStorage
 */
const getAuthToken = () => {
  const token = localStorage.getItem('access_token');
  return token;
};

/**
 * Generate carousel content using OpenAI via Django backend
 * @param {string} topic - Main topic or title for the carousel
 * @param {string} keywords - Optional comma-separated keywords
 * @param {number} slideCount - Number of slides to generate (3-10)
 * @returns {Promise<Object>} Generated slides data
 */
export const generateCarouselContent = async (topic, keywords = '', slideCount = 5) => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }

    const response = await axios.post(
      `${API_BASE_URL}/ai/generate/simple-content/`,
      {
        topic,
        keywords,
        slide_count: slideCount
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('OpenAI content generation error:', error);
    
    // Handle different error types
    if (error.response) {
      // Server responded with error
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || 'Generation failed';
      
      if (status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (status === 402) {
        throw new Error('Quota exceeded. Please upgrade your plan.');
      } else if (status === 500) {
        throw new Error(`AI generation failed: ${message}`);
      } else {
        throw new Error(message);
      }
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else went wrong
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Test connection to Django backend
 * @returns {Promise<boolean>} True if backend is accessible
 */
export const testBackendConnection = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/health/`);
    return response.status === 200;
  } catch (error) {
    console.error('Backend connection test failed:', error);
    return false;
  }
};

const openaiService = {
  generateCarouselContent,
  testBackendConnection
};

export default openaiService;
